import { Button, Flex, Modal, Search, Typography } from '@arkestro/arkestro-design-system';
import { ChangeEvent, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { CloseOutlined, FileDoneOutlined, SearchOutlined } from '@ant-design/icons';
import { usePriceAgreement, usePriceAgreements } from '@components/Agreements/api/usePriceAgreements';
import { useDebounce } from '@hooks/useDebounce';
import { NO_DATA_DISPLAY } from '@utils/data';
import styles from './PriceAgreementPanel.module.scss';
import sharedStyles from '@components/RfxDrawers/shared/SharedStyles.module.scss';

const PRICE_AGREEMENTS_CREATE_URL = '/bid_requests?tab=agreements&openCreate=true';

interface PriceAgreementPanelProps {
  locked?: boolean;
}

interface PanelDescriptionProps {
  locked: boolean;
  onCreateClick: () => void;
}

/** A locked panel only describes itself; an open one also offers to create an agreement. */
const PanelDescription = ({ locked, onCreateClick }: PanelDescriptionProps) => {
  const { t } = useTranslation('common');

  if (locked) return <>{t('rfx_drawer.price_agreement_panel_description')}</>;

  return (
    <Trans
      components={{
        link: (
          <Button
            className={styles.createNowLink}
            data-testid='create-now-link'
            onClick={onCreateClick}
            type='link'
          />
        ),
      }}
      i18nKey='common:rfx_drawer.price_agreement_panel_description_with_create_link'
    />
  );
};

export const PriceAgreementPanel = ({ locked = false }: PriceAgreementPanelProps) => {
  const { t } = useTranslation('common');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 250);
  const { data } = usePriceAgreements({ search: debouncedSearchQuery, status: 'active' });
  const { control } = useFormContext<{ priceAgreementId: number | null }>();
  const { field } = useController({
    control,
    name: 'priceAgreementId',
    rules: { required: true },
  });

  const options = useMemo(
    () => (data?.price_agreements ?? []).map(pa => ({ division: pa.corporate_division.name ?? NO_DATA_DISPLAY, label: pa.name, value: pa.id })),
    [data?.price_agreements]
  );

  const selectedInOptions = field.value
    ? options.find(opt => opt.value === field.value)
    : undefined;

  const { data: selectedAgreementData } = usePriceAgreement(
    selectedInOptions ? null : field.value
  );

  const seededOption = selectedAgreementData
    ? { division: selectedAgreementData.corporate_division.name ?? NO_DATA_DISPLAY, label: selectedAgreementData.name, value: selectedAgreementData.id }
    : undefined;

  const selectedOption = field.value
    ? selectedInOptions ?? seededOption
    : undefined;

  const handleSelect = (id: number | string) => {
    field.onChange(Number(id));
    setSearchQuery('');
  };

  const handleClear = () => {
    field.onChange(null);
  };

  let content;
  if (selectedOption) {
    content = (
      <div className={styles.selectedOption} data-testid='selected-price-agreement'>
        <div className={styles.agreementIcon}>
          <FileDoneOutlined />
        </div>
        <div className={styles.agreementDetails}>
          <span className={styles.agreementName}><strong>{selectedOption.label}</strong></span>
          <span className={styles.agreementDivision}>{selectedOption.division}</span>
        </div>
        {!locked && (
          <Button
            aria-label={t('general.clear')}
            data-testid='clear-price-agreement-button'
            icon={<CloseOutlined />}
            onClick={handleClear}
            type='text'
          />
        )}
      </div>
    );
  } else {
    content = (
      <>
        <Search
          defaultProps={{
            searchField: {
              allowClear: true,
              onChange: (e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value),
              value: searchQuery,
            },
          }}
          size='middle'
        />
        {searchQuery && options.length === 0 && (
          <Flex align='center' className={styles.emptyState} gap={8} justify='center'>
            <SearchOutlined className={styles.emptyStateIcon} />
            <Typography className={styles.text}>{t('general.no_results')}</Typography>
          </Flex>
        )}
        {searchQuery && options.map(option => (
          <div
            key={option.value}
            className={styles.agreementOption}
            data-testid={`price-agreement-option-${option.value}`}
            onClick={() => handleSelect(option.value)}
            onKeyDown={e => e.key === 'Enter' && handleSelect(option.value)}
            role='button'
            tabIndex={0}
          >
            <div className={styles.agreementIcon}>
              <FileDoneOutlined />
            </div>
            <div className={styles.agreementDetails}>
              <span className={styles.agreementName}><strong>{option.label}</strong></span>
              <span className={styles.agreementDivision}>{option.division}</span>
            </div>
            <span className={styles.selectAction}>{t('general.select')}</span>
          </div>
        ))}
      </>
    );
  }

  return (
    <Flex className={styles.panel} gap={20} vertical>
      <Flex vertical>
        <Typography className={sharedStyles.panelHeader}>
          {t('rfx_drawer.price_agreement_panel_title')}
          <span className={sharedStyles.required}> *</span>
        </Typography>
        <Typography className={styles.text}>
          <PanelDescription locked={locked} onCreateClick={() => setIsCreateModalOpen(true)} />
        </Typography>
      </Flex>
      <Flex gap={4} vertical>
        {content}
      </Flex>
      <Modal
        okText={t('rfx_drawer.price_agreement_panel_create_modal_confirm')}
        onCancel={() => setIsCreateModalOpen(false)}
        onOk={() => {
          window.open(PRICE_AGREEMENTS_CREATE_URL, '_blank', 'noopener,noreferrer');
          setIsCreateModalOpen(false);
        }}
        open={isCreateModalOpen}
        title={t('rfx_drawer.price_agreement_panel_create_modal_title')}
      >
        <Typography.Text>{t('rfx_drawer.price_agreement_panel_create_modal_content')}</Typography.Text>
      </Modal>
    </Flex>
  );
};
